import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _isObject from 'lodash/isObject';

import { cbBankFields } from 'cbbank/inquiry/fields';
import { useInquiryLabelTranslator } from 'modules/Inquiry/inquiryFieldsTranslations/useInquiryLabelTranslator';
import { useInquiryValueTranslator } from 'modules/Inquiry/inquiryFieldsTranslations/useInquiryValueTranslator';
import { ArrayPropertiesToSummaryType } from 'new/form/common/types';
import { SummaryType, unitFormatters, unitTypeSuffixMap } from 'new/form/formatters/units';
import { StepSequence, SummaryFieldsType } from 'new/form/model';
import { AnyFields } from 'new/form/state/types';
import { useGeneratePagesWithSectionsAndFieldNames } from 'new/summary/hooks/useGeneratePagesWithSectionsAndFieldNames';
import { useSummaryStepContext } from 'new/summary/SummaryPageContext';
import { useTranslations } from 'utils/hooks/useTranslations';

const CB_BANK_FACTORING_LINE_KEY = cbBankFields.offerPage.contractLaneSection.factoringLine;
const CB_BANK_PAYOUT_RATIO_KEY = cbBankFields.offerPage.contractLaneSection.payoutRatio;
const CB_BANK_FACTORING_FEE_KEY = cbBankFields.offerPage.contractLaneSection.factoringFee;

export type SummaryPageField = {
  text: string;
  caption: string;
  testId: string;
  order: number | undefined;
  render: ((text: string) => React.ReactNode) | undefined;
};

export type SummarySection = {
  [sectionName: string]: Array<SummaryPageField | Array<SummaryPageField>>;
};

export type SummaryPage = {
  [pageName: string]: SummarySection;
};

export type FieldsForSummaryPage = SummaryPage;

// Function to handle custom value getting if needed
const getFieldValue = (values: AnyFields, field: string) => {
  if (
    (field === CB_BANK_FACTORING_LINE_KEY || field === CB_BANK_PAYOUT_RATIO_KEY) &&
    !_get(values, CB_BANK_FACTORING_FEE_KEY)
  ) {
    return undefined;
  }

  return _get(values, field);
};

/**
 * On the inquiry summary page and the inquiry detail page in the operations / customer portal we need to display the
 * label and the value of the fields - additionally the label should be translated and the value should be formatted
 * according to a unit mapping from fieldName to unitFormatter. This hook generates an object with the pageName as key
 * and an array of SummaryPageField as value.
 */
export function useBuildFieldsForSummaryPage({
  stepSequence,
  summaryFields,
  fields,
}: {
  summaryFields: SummaryFieldsType<any> | undefined;
  stepSequence: StepSequence<any> | undefined;
  fields: AnyFields;
}): FieldsForSummaryPage {
  const t = useTranslations();
  const labelTranslator = useInquiryLabelTranslator();
  const valueTranslator = useInquiryValueTranslator();
  const { values } = useSummaryStepContext();
  const pagesWithSectionsAndFieldNames = useGeneratePagesWithSectionsAndFieldNames(
    stepSequence,
    fields,
  );

  function translateField(values: AnyFields, field: string, arrayField?: string) {
    let fieldValue;
    let summaryType;

    if (arrayField) {
      fieldValue = getFieldValue(values, arrayField);
    } else {
      fieldValue = getFieldValue(values, field);
    }
    summaryType = _get(summaryFields, field) as SummaryType;

    const shouldShow = summaryType?.showEmpty ? true : !_isNil(fieldValue);

    if (summaryType && shouldShow) {
      const { unit, showOnSummaryPage, order, render } = summaryType;
      if (showOnSummaryPage) {
        const unitFormatter = unitFormatters[unit];
        const translatedValue = valueTranslator(fieldValue, field);
        const formattedValue = unitFormatter
          ? unitFormatter(translatedValue, false)
          : translatedValue || '-';
        const suffix = unitTypeSuffixMap[unit](t);
        const label = labelTranslator(field);
        const testId = field;

        const isEmpty = _isNil(fieldValue) || fieldValue === '';
        const text = !isEmpty ? `${formattedValue} ${suffix || ''}` : '-';

        return {
          text,
          caption: label,
          order,
          testId,
          render,
        };
      }
    }
    return undefined;
  }

  if (stepSequence && summaryFields) {
    return Object.entries(pagesWithSectionsAndFieldNames).reduce(
      (accumulator, [pageName, sections]) => {
        const sectionsWithFields = Object.entries(sections).reduce(
          (sectionsAccumulator, [sectionName, fields]) => {
            const fieldsWithCaptionAndValue: Array<
              SummaryPageField | Array<SummaryPageField | undefined> | undefined
            > = fields
              .map((field) => {
                if (typeof field === 'string') {
                  return translateField(values, field);
                } else if (typeof field === 'object') {
                  const nestedValues = _get(values, field.fieldName);
                  const nestedFields = _get(summaryFields, field.fieldName);
                  if (Array.isArray(nestedValues)) {
                    const result = nestedValues.map((arrayObject: any, index) => {
                      if (arrayObject) {
                        return Object.keys(nestedFields).map((key) => {
                          const objectField = `${field.fieldName}.${key}`;
                          const arrayField = `${field.fieldName}[${index}].${key}`;
                          return translateField(values, objectField, arrayField);
                        });
                      }

                      return undefined;
                    });

                    const definedResult = result.map((subArray) =>
                      subArray?.filter((item) => item !== undefined),
                    );

                    if (
                      (_get(summaryFields, field.fieldName) as ArrayPropertiesToSummaryType<any>)
                        ?.isOneField
                    ) {
                      // Get all unique fields to merge
                      const uniqueFields = new Set<string>();
                      for (const subArray of definedResult) {
                        if (subArray) {
                          for (const item of subArray) {
                            if (item) {
                              uniqueFields.add(item.testId);
                            }
                          }
                        }
                      }

                      // Merge the fields with the same testId
                      for (const field of Array.from(uniqueFields)) {
                        let mergedField = {} as SummaryPageField;
                        for (const subArray of definedResult) {
                          if (subArray) {
                            const fieldObject = subArray.find((item) => item?.testId === field);
                            if (fieldObject) {
                              mergedField = {
                                ...mergedField,
                                text: mergedField.text
                                  ? `${mergedField.text.trim()}, ${fieldObject.text.trim()}`
                                  : fieldObject.text,
                              };
                            }
                          }
                        }
                        // Get the caption for the array field (also used in the inquiry as caption)
                        mergedField.caption = labelTranslator(field);
                        return mergedField;
                      }
                    }
                    return definedResult;
                  } else if (_isObject(nestedValues)) {
                    return Object.keys(nestedFields).map((key) => {
                      const objectField = `${field.fieldName}.${key}`;
                      return translateField(values, objectField);
                    });
                  }

                  return undefined;
                }

                return undefined;
              })
              .flat()
              .sort((a, b) => {
                if (!Array.isArray(a) && !Array.isArray(b)) {
                  if (a?.order && b?.order) {
                    return a.order - b.order;
                  }
                  return 0;
                }

                return 0;
              })
              .filter(Boolean);

            if (fieldsWithCaptionAndValue.length > 0) {
              return {
                ...sectionsAccumulator,
                [sectionName]: fieldsWithCaptionAndValue,
              };
            }

            return sectionsAccumulator;
          },
          {},
        );

        return {
          ...accumulator,
          [pageName]: sectionsWithFields,
        };
      },
      {},
    );
  }

  return {};
}
